/** Карточка склада ФБС в форме настройки — ЧИСТАЯ модель черновика и его проверки.
 *
 *  Ссылки 1С (склад-получатель, склад-источник, организация) человек больше не печатает: их
 *  подставляют пикеры живых справочников (`/supply/fbs/refs/*`). Проверка всё равно смотрит на
 *  GUID, а не на «выбрано в списке»: за пикером оставлен ручной ввод (1С недоступна — работа не
 *  должна вставать), и оба пути обязаны приводить к одному и тому же: валидная ссылка + имя.
 *
 *  Без молчаливых фоллбэков: пустая или кривая ссылка не подменяется ничем — форма не даёт
 *  сохранить и называет причину.
 */
import type { FbsChannel, FbsStockSource, FbsWarehouseBody } from "@/api/supply";

/** Черновик = тело склада, но источник остатка может быть ещё НЕ выбран (`""`): у нового склада
 *  его не подставляем — Москва и регионы считаются по-разному, и угаданный источник дал бы то
 *  самое расхождение «ЛК WB ≠ портал». */
export type WarehouseDraft = Omit<FbsWarehouseBody, "stock_source"> & {
  stock_source: FbsStockSource | "";
};

export const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const EMPTY_CHANNEL: FbsChannel = {
  mp_code: "wb",
  legal_name: "",
  mp_warehouse_id: null,
  mp_warehouse_name: null,
};

export const EMPTY_DRAFT: WarehouseDraft = {
  name: "",
  target_wh_ref: "",
  target_wh_name: "",
  source_wh_ref: "",
  source_wh_name: "",
  org_key: "",
  channels: [{ ...EMPTY_CHANNEL }],
  stock_source: "",
  active: true,
};

/** Ключ канала для поиска дублей — ТРОЙКА (площадка, юрлицо, склад площадки), как на бэке:
 *  «всё юрлицо» и «его же склад» — разные каналы, а один и тот же склад дважды — дубль. */
export function channelKey(c: FbsChannel): string {
  return `${c.mp_code}|${c.legal_name.trim()}|${c.mp_warehouse_id ?? ""}`;
}

/** Что не так с карточкой. Пусто — можно сохранять. */
export function draftErrors(d: WarehouseDraft): string[] {
  const errs: string[] = [];
  if (!d.name.trim()) errs.push("название склада пустое");
  for (const [field, label] of [
    ["target_wh_ref", "склад-получатель"],
    ["source_wh_ref", "склад-источник"],
    ["org_key", "организация"],
  ] as const)
    if (!GUID_RE.test(d[field]))
      errs.push(`${label}: не выбран (или введён не GUID 1С — 36 символов)`);
  if (!d.target_wh_name.trim()) errs.push("имя склада-получателя пустое");
  if (!d.source_wh_name.trim()) errs.push("имя склада-источника пустое");
  if (d.stock_source === "") errs.push("не выбран источник остатка ФБС");
  if (d.channels.length === 0) errs.push("нужен хотя бы один канал");
  if (d.channels.some((c) => !c.legal_name.trim())) errs.push("у канала не выбрано юрлицо");
  // Склад продавца портал читает только у WB — у Ozon его и выбрать негде; такой канал бэк
  // отвергнет, поэтому говорим об этом здесь, а не после «Сохранить».
  if (d.channels.some((c) => c.mp_warehouse_id != null && c.mp_code !== "wb"))
    errs.push("склад площадки поддержан только для WB");
  if (d.channels.some((c) => c.mp_warehouse_id == null && c.mp_warehouse_name))
    errs.push("у канала указано имя склада площадки без самого склада");
  const keys = d.channels.map(channelKey);
  if (new Set(keys).size !== keys.length) errs.push("каналы дублируются");
  return errs;
}

/** Имя склада площадки при выборе в пикере (`MpWarehouseSelect`): id найден в списке — берём
 *  имя оттуда; id не найден, но НЕ изменился (список ещё грузится или сеть моргнула) —
 *  сохраняем ТЕКУЩЕЕ имя канала, а не затираем его в пустую строку. Id действительно сменился,
 *  а нового имени в списке нет — тогда и правда неоткуда взять имя, кроме "". */
export function resolveMpWarehouseName(
  id: number | null,
  current: { id: number | null; name: string | null },
  list: { id: number; name: string }[],
): string {
  if (id === null) return "";
  const found = list.find((w) => w.id === id);
  if (found) return found.name;
  return id === current.id ? current.name ?? "" : "";
}

/** Тело запроса: обрезаем пробелы там, где человек мог их оставить. Источник остатка не выбран —
 *  исключение (кнопка «Сохранить» при этом выключена `draftErrors`), а не подстановка. */
export function toBody(d: WarehouseDraft): FbsWarehouseBody {
  const source = d.stock_source;
  if (source === "") throw new Error("Склад ФБС: не выбран источник остатка ФБС");
  return {
    ...d,
    stock_source: source,
    name: d.name.trim(),
    target_wh_name: d.target_wh_name.trim(),
    source_wh_name: d.source_wh_name.trim(),
    channels: d.channels.map((c) => ({ ...c, legal_name: c.legal_name.trim() })),
  };
}
